import { Landmark, AlertTriangle, Plus, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

// Dialog di riepilogo della sincronizzazione Catasto: mostra modifiche proposte, immobili
// non associati e diagnostica CF, con conferma/annulla. Guidato interamente da `catastoSync`.
export const CatastoProposalDialog = ({ catastoSync, addedUnassigned, openAssoc }) => (
  <Dialog open={!!catastoSync.proposal} onOpenChange={(o) => { if (!o) catastoSync.discardProposal(); }}>
    <DialogContent className="max-w-2xl max-h-[90vh] !flex flex-col" data-testid="catasto-proposal-dialog">
      <DialogHeader className="shrink-0">
        <DialogTitle className="flex items-center gap-2"><Landmark className="h-5 w-5 text-[#007BFF]" />Riepilogo sincronizzazione catasto</DialogTitle>
        <DialogDescription>
          Controlla le modifiche prima di applicarle all'anagrafica. I nominativi che <b>non erano presenti</b> verranno aggiunti.
        </DialogDescription>
      </DialogHeader>
      {catastoSync.proposal && (
        <div className="flex-1 min-h-0 overflow-y-auto scroll-thin pr-0.5" data-testid="catasto-proposal-body">
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-700 border border-emerald-200 font-medium">{catastoSync.proposal.to_create} da aggiungere</span>
            <span className="px-2 py-1 rounded bg-sky-100 text-sky-700 border border-sky-200 font-medium">{catastoSync.proposal.to_update} da aggiornare</span>
            <span className="px-2 py-1 rounded bg-secondary text-muted-foreground border border-border">{(catastoSync.proposal.units || []).length} immobili</span>
          </div>
          {(catastoSync.proposal.paid_calls != null || catastoSync.proposal.cached_calls != null) && (
            <div className="mt-2 text-xs px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="catasto-cost-summary">
              <span className="font-medium text-slate-700">Riepilogo ricerche</span>
              <span className="text-slate-600">Codici fiscali interrogati: <b>{catastoSync.proposal.paid_calls ?? 0}</b></span>
              <span className="text-emerald-700">Da cache: <b>{catastoSync.proposal.cached_calls ?? 0}</b></span>
              <span className="text-[#007BFF]">Addebito: <b>3 SedioCoin</b></span>
            </div>
          )}
          {catastoSync.proposal.no_cf_error ? (
            <div className="mt-3 text-xs px-3 py-2 rounded-lg bg-red-50 text-red-700 border border-red-200" data-testid="catasto-nocf-error">
              Ricerca per indirizzo dei condòmini senza codice fiscale non riuscita: {catastoSync.proposal.no_cf_error}
            </div>
          ) : (catastoSync.proposal.no_cf_total > 0 && catastoSync.proposal.no_cf_matched < catastoSync.proposal.no_cf_total) && (
            <div className="mt-3 text-xs px-3 py-2 rounded-lg bg-amber-50 text-amber-700 border border-amber-200" data-testid="catasto-nocf-warning">
              {catastoSync.proposal.no_cf_matched} di {catastoSync.proposal.no_cf_total} condòmini senza C.F. abbinati per nome.
              Per i restanti l'immobile non è stato trovato all'indirizzo: verifica che il <b>nome in anagrafica</b> combaci con quello del Catasto o inserisci il <b>codice fiscale</b>.
            </div>
          )}
          <div className="mt-3 divide-y divide-border border border-border rounded-lg" data-testid="catasto-proposal-list">
            {(catastoSync.proposal.changes || []).map((ch, ci) => (
              <div key={ci} className="px-3 py-2.5 flex items-start justify-between gap-3" data-testid={`proposal-row-${ci}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm flex items-center gap-1.5 flex-wrap">
                    {ch.nome}
                    {ch.action === "create"
                      ? <span className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">nuovo</span>
                      : <span className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 border border-sky-200">aggiorna · {ch.match}</span>}
                    {ch.interno && <span className="text-[10px] text-muted-foreground">int. {ch.interno}</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                    {ch.codice_fiscale
                      ? <span className="font-mono">{ch.codice_fiscale}{ch.cf_source === "calcolato" ? " · calcolato" : ch.cf_source === "verificato" ? " · verificato ✓" : ""}</span>
                      : <span className="text-amber-600">senza C.F.</span>}
                    <span>· {(ch.immobili || []).map((u) => `Sub.${u.subalterno || "—"}${u.categoria ? " " + u.categoria : ""}`).join(", ")}</span>
                  </div>
                </div>
              </div>
            ))}
            {(catastoSync.proposal.changes || []).length === 0 && (
              <div className="px-3 py-4 text-sm text-muted-foreground text-center">Nessuna modifica proposta.</div>
            )}
          </div>
          {(catastoSync.proposal.unassigned_units || []).length > 0 && (
            <div className="mt-3 border border-amber-300 rounded-lg overflow-hidden" data-testid="catasto-unassigned">
              <div className="px-3 py-2 bg-amber-50 text-amber-800 text-xs font-semibold flex items-center gap-1.5 border-b border-amber-200">
                <AlertTriangle className="h-3.5 w-3.5" />
                Immobili al civico non associati a nessun condòmino ({catastoSync.proposal.unassigned_units.length})
              </div>
              <div className="divide-y divide-border">
                {catastoSync.proposal.unassigned_units.map((u, ui) => (
                  <div key={ui} className="px-3 py-2 text-[11px]" data-testid={`unassigned-row-${ui}`}>
                    <div className="font-medium text-foreground">
                      Fg. {u.foglio || "—"} · Part. {u.particella || "—"} · Sub. {u.subalterno || "—"}
                      {u.categoria ? ` · ${u.categoria}` : ""}
                    </div>
                    {(u.intestatari || []).length > 0 ? (
                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                        {u.intestatari.map((t, ti) => {
                          const key = `${ui}-${ti}`;
                          const added = !!addedUnassigned[key];
                          return (
                            <div key={ti} className="flex items-center gap-1">
                              <span className="text-muted-foreground">
                                <b className="text-foreground">{t.nome || "—"}</b>
                                {t.cf ? <span className="font-mono"> ({t.cf})</span> : ""}
                                {t.quota ? ` · ${t.quota}` : ""}
                              </span>
                              {t.nome && (
                                added
                                  ? <span className="text-emerald-600 text-[10px] font-medium whitespace-nowrap">✓ Aggiunto</span>
                                  : <Button size="sm" variant="outline" data-testid={`add-unassigned-${ui}-${ti}`}
                                      className="h-6 px-2 text-[10px] border-emerald-300 text-emerald-700 hover:bg-emerald-50 whitespace-nowrap"
                                      onClick={() => openAssoc(u, t, key)}>
                                      <Plus className="h-3 w-3 mr-0.5" />Aggiungi
                                    </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="mt-0.5 text-muted-foreground">Intestatario non disponibile</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
            <details className="mt-3 text-xs border border-border rounded-lg" data-testid="catasto-cf-diagnostics">
              <summary className="px-3 py-2 cursor-pointer select-none text-muted-foreground hover:text-foreground">
                Dettagli tecnici: perché un comproprietario è (o non è) stato cercato
              </summary>
              <div className="px-3 pb-2 divide-y divide-border">
                {(catastoSync.proposal.cf_diagnostics || []).map((dg, di) => (
                  <div key={di} className="py-2" data-testid={`cf-diag-${dg.cf}`}>
                    <div className="font-medium">{dg.nome} <span className="font-mono text-muted-foreground">{dg.cf}</span></div>
                    {dg.errore && <div className="text-red-600">{dg.errore}</div>}
                    {(dg.immobili || []).length === 0 && !dg.errore && (
                      <div className="text-amber-600">Nessun immobile trovato a questo indirizzo per questo C.F.</div>
                    )}
                    {dg.ricerca_nome && dg.ricerca_nome.esito === "trovato" && (
                      <div className="text-sky-700 mt-0.5" data-testid={`cf-diag-name-${dg.cf}`}>
                        Ricerca per nome: immobile trovato al civico
                        {dg.ricerca_nome.intestatari && dg.ricerca_nome.intestatari.length > 0
                          ? <> · intestatario/i: <b>{dg.ricerca_nome.intestatari.join(", ")}</b></>
                          : null}
                      </div>
                    )}
                    {dg.ricerca_nome && dg.ricerca_nome.esito === "non_trovato" && (
                      <div className="text-muted-foreground mt-0.5" data-testid={`cf-diag-name-${dg.cf}`}>
                        Ricerca per nome al civico: nessuna corrispondenza → l'immobile risulta intestato a un altro soggetto (es. familiare) oppure il condòmino è inquilino.
                      </div>
                    )}
                    {(dg.immobili || []).map((im, ii) => (
                      <div key={ii} className="text-muted-foreground mt-0.5">
                        Imm. <span className="font-mono">{im.fps}</span> · titolarità: <i>{im.titolarita || "non riportata"}</i>
                        {" · "}{im.cerca_comproprietari
                          ? <span className="text-sky-600">comproprietari cercati ({im.comproprietari_trovati || 0} trovati)</span>
                          : <span className="text-emerald-600">100% → nessun comproprietario</span>}
                        {(im.intestatari && im.intestatari.length > 0) && (
                          <div className="ml-3 mt-0.5 border-l border-border pl-2">
                            {im.intestatari.map((t, ti) => (
                              <div key={ti} className="text-[11px]">
                                • {t.nome || <span className="text-red-600">nome non letto</span>}
                                {t.cf ? <span className="font-mono"> ({t.cf})</span> : <span className="text-amber-600"> (CF assente)</span>}
                                {t.quota ? ` · ${t.quota}` : ""}
                                {!t.nome && t.campi ? <span className="text-muted-foreground"> · campi: {t.campi.join(", ")}</span> : ""}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </details>
        </div>
      )}
      <DialogFooter className="shrink-0">
        <Button variant="outline" data-testid="catasto-proposal-cancel" disabled={catastoSync.applying} onClick={() => catastoSync.discardProposal()}>Annulla</Button>
        <Button data-testid="catasto-proposal-confirm" className="bg-[#007BFF] hover:bg-[#0056B3]" disabled={catastoSync.applying || !(catastoSync.proposal?.changes || []).length} onClick={() => catastoSync.applyProposal()}>
          {catastoSync.applying ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}Conferma e aggiorna
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
